import { ATTR } from '@gonzovnc/protocol';
import type { InputEncoder } from './input-encoder.js';
import type { DisplayInfo } from '@gonzovnc/protocol';

export interface ControlClientEvents {
  onCaptureEnabled: (displays: DisplayInfo[]) => void;
  onCaptureDisabled: (reason: string) => void;
  onRoleChanged: (role: string) => void;
}

/**
 * Client-side control state (§6.3/§6.9). Capture is armed ONLY after the Host's `hello`
 * arrives for an epoch matching our server-stamped controller attribute — never on the
 * attribute change alone. Losing the controller role (attributes or `bye`) disarms
 * immediately and resets the encoder so no stuck input is left mid-gesture.
 */
export class ControlClient {
  private role = 'viewer';
  private myEpoch = 0;
  private capture = false;

  constructor(
    private myIdentity: string,
    private encoder: InputEncoder,
    private events: ControlClientEvents,
  ) {}

  get isController(): boolean { return this.role === 'controller'; }
  get captureEnabled(): boolean { return this.capture; }
  get epoch(): number { return this.myEpoch; }

  /** Own attributes changed (server-stamped). */
  onMyAttributes(attrs: Record<string, string>): void {
    const newRole = attrs[ATTR.ROLE] ?? 'viewer';
    const epochStr = attrs[ATTR.CTL_EPOCH];
    if (epochStr) this.myEpoch = Number(epochStr);
    if (newRole !== this.role) {
      this.role = newRole;
      this.events.onRoleChanged(newRole);
      if (newRole !== 'controller') this.disableCapture('role-lost');
      // becoming controller: wait for hello before enabling capture
    }
  }

  /** Host said hello for epoch `e`. Arm capture only if it matches our controller epoch. */
  onHello(e: number, displays: DisplayInfo[]): void {
    if (this.role !== 'controller') return;
    if (e !== this.myEpoch) return; // stale hello; ignore
    this.encoder.setEpoch(e);
    this.capture = true;
    this.events.onCaptureEnabled(displays);
  }

  onBye(reason: string): void {
    this.disableCapture(reason);
  }

  private disableCapture(reason: string): void {
    if (!this.capture) return;
    this.capture = false;
    this.events.onCaptureDisabled(reason);
  }
}
